/**
 * TeamStrength Hub - Team Detail Page
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { teamApi, modelApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  ArrowLeft, Users, Play, Archive, UserPlus, Sparkles, Loader2,
  Grid3X3, FileText, Trash2, CheckCircle2, Clock, Heart, Globe,
  Lightbulb, Eye, EyeOff, RefreshCw, Zap, Link2
} from 'lucide-react';
import TeamGridPro from '../components/TeamGridPro';

// Simple markdown renderer
const MarkdownContent = ({ content }) => {
  return (
    <div className="prose prose-slate dark:prose-invert max-w-none prose-headings:font-heading prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg">
      {content.split('\n').map((line, i) => {
        if (line.startsWith('# ')) {
          return <h1 key={i} className="text-2xl font-bold mt-6 mb-3" style={{ fontFamily: 'Manrope' }}>{line.slice(2)}</h1>;
        }
        if (line.startsWith('## ')) {
          return <h2 key={i} className="text-xl font-bold mt-5 mb-2" style={{ fontFamily: 'Manrope' }}>{line.slice(3)}</h2>;
        }
        if (line.startsWith('### ')) {
          return <h3 key={i} className="text-lg font-semibold mt-4 mb-2" style={{ fontFamily: 'Manrope' }}>{line.slice(4)}</h3>;
        }
        if (line.startsWith('- ')) {
          return <li key={i} className="ml-4">{line.slice(2)}</li>;
        }
        if (line.startsWith('**') && line.endsWith('**')) {
          return <p key={i} className="font-semibold my-2">{line.slice(2, -2)}</p>;
        }
        if (line.trim() === '') {
          return <br key={i} />;
        }
        return <p key={i} className="my-2">{line}</p>;
      })}
    </div>
  );
};

const TeamDetailPage = () => {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { currentOrg, user } = useAuth();
  
  const [team, setTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [gridData, setGridData] = useState(null);
  const [reports, setReports] = useState([]);
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('grid');
  
  // Dialog states
  const [showAddMemberDialog, setShowAddMemberDialog] = useState(false);
  const [showStrengthDialog, setShowStrengthDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedStrengths, setSelectedStrengths] = useState([]);
  
  // Generation states
  const [generating, setGenerating] = useState({
    team_analysis: false,
    playbook: false,
    meeting_impulses: false
  });
  
  // Team Chemistry states
  const [chemistryLanguage, setChemistryLanguage] = useState('de');
  const [chemistryData, setChemistryData] = useState(null);
  const [generatingChemistry, setGeneratingChemistry] = useState(false);

  const fetchTeamData = useCallback(async () => {
    if (!currentOrg || !teamId) return;
    
    try {
      setLoading(true);
      const [teamRes, membersRes, gridRes, reportsRes, modelRes] = await Promise.all([
        teamApi.get(currentOrg.org_id, teamId),
        teamApi.listMembers(currentOrg.org_id, teamId),
        teamApi.getGrid(currentOrg.org_id, teamId),
        teamApi.listReports(currentOrg.org_id, teamId),
        modelApi.get(currentOrg.org_id)
      ]);
      
      setTeam(teamRes.data);
      setMembers(membersRes.data);
      setGridData(gridRes.data);
      setReports(reportsRes.data);
      setModel(modelRes.data);
    } catch (error) {
      toast.error('Failed to load team data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentOrg, teamId]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  const handleStatusChange = async (newStatus) => {
    try {
      await teamApi.update(currentOrg.org_id, teamId, { status: newStatus });
      toast.success(`Team ${newStatus === 'active' ? 'activated' : 'archived'}!`);
      fetchTeamData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  const handleGenerateReport = async (reportType) => {
    setGenerating(prev => ({ ...prev, [reportType]: true }));
    try {
      const response = await teamApi.generateReport(currentOrg.org_id, teamId, reportType);
      toast.success('Report generated!');
      setReports(prev => [{ ...response.data, report_type: reportType, created_at: new Date().toISOString() }, ...prev]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate report');
    } finally {
      setGenerating(prev => ({ ...prev, [reportType]: false }));
    }
  };

  // Load cached chemistry analysis
  useEffect(() => {
    const loadChemistry = async () => {
      if (!currentOrg?.org_id || !teamId) return;
      try {
        const res = await teamApi.getChemistry(currentOrg.org_id, teamId, chemistryLanguage);
        if (res.data && res.data.summary) {
          setChemistryData(res.data);
        } else {
          setChemistryData(null);
        }
      } catch (error) {
        console.error('Failed to load chemistry data:', error);
      }
    };
    loadChemistry();
  }, [currentOrg?.org_id, teamId, chemistryLanguage]);

  const generateTeamChemistry = async () => {
    if (!currentOrg?.org_id) return;
    
    setGeneratingChemistry(true);
    try {
      const response = await teamApi.generateChemistry(currentOrg.org_id, teamId, {
        language: chemistryLanguage
      });
      setChemistryData(response.data);
      toast.success(chemistryLanguage === 'de' ? 'Team-Chemie-Analyse erstellt!' : 'Team chemistry analysis generated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate analysis');
    } finally {
      setGeneratingChemistry(false);
    }
  };

  const toggleChemistryLanguage = () => {
    const newLang = chemistryLanguage === 'de' ? 'en' : 'de';
    setChemistryLanguage(newLang);
    setChemistryData(null);
  };

  const openStrengthEditor = (member) => {
    setSelectedMember(member);
    // Pre-populate with existing strengths
    const existingStrengths = member.strengths?.map(s => ({
      talent_id: s.talent_id,
      rank: s.rank
    })) || [];
    setSelectedStrengths(existingStrengths);
    setShowStrengthDialog(true);
  };

  const handleSaveStrengths = async () => {
    if (!selectedMember) return;
    
    try {
      await teamApi.saveStrengths(currentOrg.org_id, teamId, {
        team_id: teamId,
        user_id: selectedMember.user_id,
        strengths: selectedStrengths
      });
      toast.success('Strengths saved!');
      setShowStrengthDialog(false);
      fetchTeamData();
    } catch (error) {
      toast.error('Failed to save strengths');
    }
  };

  const addStrength = (talentId) => {
    if (selectedStrengths.length >= 10) {
      toast.error('Maximum 10 strengths allowed');
      return;
    }
    if (selectedStrengths.find(s => s.talent_id === talentId)) {
      toast.error('Strength already added');
      return;
    }
    setSelectedStrengths(prev => [...prev, { talent_id: talentId, rank: prev.length + 1 }]);
  };

  const removeStrength = (talentId) => {
    setSelectedStrengths(prev => {
      const filtered = prev.filter(s => s.talent_id !== talentId);
      return filtered.map((s, i) => ({ ...s, rank: i + 1 }));
    });
  };

  const moveStrength = (talentId, direction) => {
    setSelectedStrengths(prev => {
      const index = prev.findIndex(s => s.talent_id === talentId);
      if (index === -1) return prev;
      if (direction === 'up' && index === 0) return prev;
      if (direction === 'down' && index === prev.length - 1) return prev;
      
      const newArr = [...prev];
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      [newArr[index], newArr[swapIndex]] = [newArr[swapIndex], newArr[index]];
      return newArr.map((s, i) => ({ ...s, rank: i + 1 }));
    });
  };

  // Get heat color based on rank
  const getHeatColor = (rank) => {
    if (!rank) return '';
    if (rank <= 2) return 'heatmap-peak';
    if (rank <= 5) return 'heatmap-high';
    if (rank <= 7) return 'heatmap-medium';
    return 'heatmap-low';
  };

  const getTalentById = (talentId) => {
    return model?.talents?.find(t => t.talent_id === talentId);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="h-64 skeleton rounded-xl" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500">Team not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate('/dashboard/teams')}>
          Back to Teams
        </Button>
      </div>
    );
  }

  const isAdmin = currentOrg?.role === 'org_admin' || members.find(m => m.user_id === user?.user_id)?.role === 'team_admin';

  return (
    <div className="space-y-6" data-testid="team-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between animate-fade-in">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/dashboard/teams')}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
                {team.name}
              </h1>
              <Badge 
                variant={team.status === 'active' ? 'default' : 'secondary'}
                className="capitalize"
              >
                {team.status}
              </Badge>
            </div>
            {team.description && (
              <p className="text-slate-500 dark:text-slate-400 mt-1">{team.description}</p>
            )}
          </div>
        </div>
        
        {isAdmin && (
          <div className="flex items-center gap-2">
            {team.status === 'draft' && (
              <Button 
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => handleStatusChange('active')}
                data-testid="activate-team-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                Activate
              </Button>
            )}
            {team.status === 'active' && (
              <Button 
                variant="outline"
                onClick={() => handleStatusChange('archived')}
                data-testid="archive-team-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Archive
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="animate-fade-in stagger-1">
        <TabsList>
          <TabsTrigger value="grid" data-testid="tab-grid">
            <Grid3X3 className="w-4 h-4 mr-2" />
            Team Grid
          </TabsTrigger>
          <TabsTrigger value="members" data-testid="tab-members">
            <Users className="w-4 h-4 mr-2" />
            Members ({members.length})
          </TabsTrigger>
          <TabsTrigger value="chemistry" data-testid="tab-chemistry">
            <Heart className="w-4 h-4 mr-2" />
            Team-Chemie
          </TabsTrigger>
          <TabsTrigger value="ai" data-testid="tab-ai">
            <Sparkles className="w-4 h-4 mr-2" />
            AI Insights
          </TabsTrigger>
        </TabsList>

        {/* Grid Tab */}
        <TabsContent value="grid" className="mt-6">
          <TeamGridPro 
            gridData={gridData}
            members={members}
            model={model}
            onEditStrengths={openStrengthEditor}
          />
        </TabsContent>

        {/* Members Tab */}
        <TabsContent value="members" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope' }}>Team Members</CardTitle>
                  <CardDescription>Manage team members and their strengths</CardDescription>
                </div>
                {isAdmin && (
                  <Button 
                    variant="outline"
                    onClick={() => setShowAddMemberDialog(true)}
                    data-testid="add-member-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Add Member
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {members.length > 0 ? (
                <div className="space-y-3">
                  {members.map((member) => (
                    <div 
                      key={member.user_id}
                      className="flex items-center justify-between p-4 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center text-violet-600 font-medium">
                          {member.user?.name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div>
                          <p className="font-medium text-slate-700 dark:text-slate-300">
                            {member.user?.name || 'Unknown'}
                          </p>
                          <p className="text-sm text-slate-500">{member.user?.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant="secondary" className="capitalize">
                          {member.role?.replace('_', ' ')}
                        </Badge>
                        <span className="text-sm text-slate-500">
                          {member.strengths?.length || 0} strengths
                        </span>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openStrengthEditor(member)}
                          data-testid={`edit-strengths-${member.user_id}`}
                        >
                          Edit Strengths
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500">No members in this team yet</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Team Chemistry Tab */}
        <TabsContent value="chemistry" className="mt-6">
          <div className="space-y-6">
            {/* Header with Language Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white" style={{ fontFamily: 'Manrope' }}>
                  {chemistryLanguage === 'de' ? 'Team-Chemie-Analyse' : 'Team Chemistry Analysis'}
                </h2>
                <p className="text-slate-500 dark:text-slate-400">
                  {chemistryLanguage === 'de' 
                    ? 'Entdecke wie eure Stärken zusammenwirken' 
                    : 'Discover how your strengths work together'}
                </p>
              </div>
              
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={toggleChemistryLanguage}
                  className="gap-2"
                  data-testid="chemistry-language-toggle"
                >
                  <Globe className="w-4 h-4" />
                  {chemistryLanguage === 'de' ? 'English' : 'Deutsch'}
                </Button>
                
                <Button
                  onClick={generateTeamChemistry}
                  disabled={generatingChemistry || members.length < 2}
                  className="bg-pink-600 hover:bg-pink-700"
                  data-testid="generate-chemistry-btn"
                >
                  {generatingChemistry ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {chemistryLanguage === 'de' ? 'Wird generiert...' : 'Generating...'}
                    </>
                  ) : (
                    <>
                      <Heart className="w-4 h-4 mr-2" />
                      {chemistryData 
                        ? (chemistryLanguage === 'de' ? 'Neu generieren' : 'Regenerate')
                        : (chemistryLanguage === 'de' ? 'Analyse starten' : 'Generate Analysis')}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {members.length < 2 ? (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Users className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
                  <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
                    {chemistryLanguage === 'de' ? 'Mehr Teammitglieder benötigt' : 'More team members needed'}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
                    {chemistryLanguage === 'de' 
                      ? 'Füge mindestens 2 Teammitglieder mit Stärken hinzu, um die Team-Chemie-Analyse zu nutzen.'
                      : 'Add at least 2 team members with strengths to use the team chemistry analysis.'}
                  </p>
                </CardContent>
              </Card>
            ) : generatingChemistry ? (
              <div className="space-y-4">
                <Card><CardContent className="py-8"><div className="h-24 skeleton rounded" /></CardContent></Card>
                <div className="grid gap-4 md:grid-cols-2">
                  <Card><CardContent className="py-8"><div className="h-40 skeleton rounded" /></CardContent></Card>
                  <Card><CardContent className="py-8"><div className="h-40 skeleton rounded" /></CardContent></Card>
                </div>
              </div>
            ) : chemistryData ? (
              <>
                {/* Summary Card */}
                <Card className="border-pink-200 dark:border-pink-800">
                  <CardContent className="pt-6">
                    <div className="p-4 rounded-xl bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 border border-pink-100 dark:border-pink-800">
                      <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                        {chemistryData.summary}
                      </p>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 mt-4">
                      {/* Synergies */}
                      <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800">
                        <div className="flex items-center gap-2 mb-2">
                          <Zap className="w-5 h-5 text-emerald-600" />
                          <h4 className="font-semibold text-emerald-800 dark:text-emerald-300" style={{ fontFamily: 'Manrope' }}>
                            {chemistryLanguage === 'de' ? 'Synergien' : 'Synergies'}
                          </h4>
                        </div>
                        <p className="text-sm text-emerald-700 dark:text-emerald-400">{chemistryData.synergies}</p>
                      </div>
                      
                      {/* Blind Spots */}
                      <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800">
                        <div className="flex items-center gap-2 mb-2">
                          <EyeOff className="w-5 h-5 text-amber-600" />
                          <h4 className="font-semibold text-amber-800 dark:text-amber-300" style={{ fontFamily: 'Manrope' }}>
                            {chemistryLanguage === 'de' ? 'Blinde Flecken' : 'Blind Spots'}
                          </h4>
                        </div>
                        <p className="text-sm text-amber-700 dark:text-amber-400">{chemistryData.blind_spots}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Strength Pairs Section */}
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Similar Pairs */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                        <div className="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                          <Link2 className="w-4 h-4 text-blue-600" />
                        </div>
                        {chemistryLanguage === 'de' ? 'Gleiche Stärken' : 'Similar Strengths'}
                      </CardTitle>
                      <CardDescription>
                        {chemistryLanguage === 'de' 
                          ? 'Teammitglieder mit ähnlichen Stärken' 
                          : 'Team members with similar strengths'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {chemistryData.similar_pairs?.map((pair, index) => (
                        <div 
                          key={index}
                          className="p-4 rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800"
                        >
                          <div className="flex items-center justify-center gap-3 mb-3">
                            <div className="w-10 h-10 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center text-blue-700 dark:text-blue-300 font-medium text-sm">
                              {pair.member1?.charAt(0)}
                            </div>
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-300">
                              {pair.shared_strength}
                            </Badge>
                            <div className="w-10 h-10 rounded-full bg-blue-200 dark:bg-blue-800 flex items-center justify-center text-blue-700 dark:text-blue-300 font-medium text-sm">
                              {pair.member2?.charAt(0)}
                            </div>
                          </div>
                          <p className="text-center text-sm font-medium text-blue-800 dark:text-blue-300 mb-2">
                            {pair.member1} & {pair.member2}
                          </p>
                          <p className="text-sm text-blue-600 dark:text-blue-400 text-center">
                            {pair.reason}
                          </p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>

                  {/* Complementary Pairs */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: 'Manrope' }}>
                        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                          <Zap className="w-4 h-4 text-purple-600" />
                        </div>
                        {chemistryLanguage === 'de' ? 'Komplementäre Stärken' : 'Complementary Strengths'}
                      </CardTitle>
                      <CardDescription>
                        {chemistryLanguage === 'de' 
                          ? 'Teammitglieder die sich ergänzen' 
                          : 'Team members that complement each other'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {chemistryData.complementary_pairs?.map((pair, index) => (
                        <div 
                          key={index}
                          className="p-4 rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-purple-200 dark:bg-purple-800 flex items-center justify-center text-purple-700 dark:text-purple-300 font-medium text-xs">
                                {pair.member1?.charAt(0)}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-purple-800 dark:text-purple-300">{pair.member1}</p>
                                <Badge variant="outline" className="text-xs border-purple-300 text-purple-600">{pair.strength1}</Badge>
                              </div>
                            </div>
                            <Zap className="w-5 h-5 text-purple-400" />
                            <div className="flex items-center gap-2">
                              <div className="text-right">
                                <p className="text-sm font-medium text-purple-800 dark:text-purple-300">{pair.member2}</p>
                                <Badge variant="outline" className="text-xs border-purple-300 text-purple-600">{pair.strength2}</Badge>
                              </div>
                              <div className="w-8 h-8 rounded-full bg-purple-200 dark:bg-purple-800 flex items-center justify-center text-purple-700 dark:text-purple-300 font-medium text-xs">
                                {pair.member2?.charAt(0)}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-purple-600 dark:text-purple-400 text-center">
                            {pair.reason}
                          </p>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                </div>

                {/* Team Tips */}
                {chemistryData.tips?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                        <Lightbulb className="w-5 h-5 text-amber-500" />
                        {chemistryLanguage === 'de' ? 'Team-Tipps' : 'Team Tips'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid gap-4 md:grid-cols-3">
                        {chemistryData.tips.map((tip, index) => (
                          <div 
                            key={index}
                            className="p-4 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-800"
                          >
                            <div className="w-8 h-8 rounded-full bg-amber-200 dark:bg-amber-800 flex items-center justify-center mb-3">
                              <span className="text-amber-700 dark:text-amber-300 font-bold">{index + 1}</span>
                            </div>
                            <p className="text-sm text-amber-800 dark:text-amber-300">{tip}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="w-16 h-16 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center mx-auto mb-4">
                    <Heart className="w-8 h-8 text-pink-400" />
                  </div>
                  <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
                    {chemistryLanguage === 'de' ? 'Team-Chemie entdecken' : 'Discover Team Chemistry'}
                  </h3>
                  <p className="text-slate-500 dark:text-slate-400 text-center max-w-md mb-4">
                    {chemistryLanguage === 'de' 
                      ? 'Klicke auf "Analyse starten" um zu sehen, wie die Stärken deines Teams zusammenwirken.'
                      : 'Click "Generate Analysis" to see how your team\'s strengths work together.'}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Generate buttons */}
            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { type: 'team_analysis', label: 'Team Analysis', description: 'Comprehensive team strengths overview' },
                { type: 'playbook', label: 'Team Playbook', description: 'Practical team development guide' },
                { type: 'meeting_impulses', label: 'Meeting Impulses', description: 'Facilitation ideas and activities' }
              ].map(({ type, label, description }) => (
                <Card key={type} className="card-highlight">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-slate-700 dark:text-slate-300 mb-1" style={{ fontFamily: 'Manrope' }}>
                      {label}
                    </h3>
                    <p className="text-sm text-slate-500 mb-4">{description}</p>
                    <Button
                      className="w-full bg-violet-600 hover:bg-violet-700"
                      onClick={() => handleGenerateReport(type)}
                      disabled={generating[type] || members.length === 0}
                      data-testid={`generate-${type}-btn`}
                    >
                      {generating[type] ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4 mr-2" />
                          Generate
                        </>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Reports list */}
            <div className="lg:col-span-3">
              {reports.length > 0 ? (
                <div className="space-y-4">
                  {reports.map((report, index) => (
                    <Card key={report.report_id || index} className="ai-report-card">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between">
                          <Badge variant="secondary" className="capitalize">
                            {report.report_type?.replace('_', ' ')}
                          </Badge>
                          <span className="text-xs text-slate-500">
                            {new Date(report.created_at).toLocaleString()}
                          </span>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <MarkdownContent content={report.content} />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FileText className="w-16 h-16 text-slate-300 dark:text-slate-600 mb-4" />
                    <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2" style={{ fontFamily: 'Manrope' }}>
                      No reports yet
                    </h3>
                    <p className="text-slate-500 dark:text-slate-400 text-center max-w-md">
                      Generate AI insights to get actionable recommendations for your team.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Strength Editor Dialog */}
      <Dialog open={showStrengthDialog} onOpenChange={setShowStrengthDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>
              Edit Strengths - {selectedMember?.user?.name}
            </DialogTitle>
            <DialogDescription>
              Select and rank top 5-10 strengths. Drag to reorder.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 py-4">
            {/* Available talents */}
            <div>
              <Label className="mb-2 block">Available Strengths</Label>
              <ScrollArea className="h-[300px] border rounded-lg p-2">
                {model?.categories?.map((category) => (
                  <div key={category.category_id} className="mb-4">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2"
                       style={{ color: category.color }}>
                      {category.name}
                    </p>
                    <div className="space-y-1">
                      {model?.talents
                        ?.filter(t => t.category_id === category.category_id)
                        .map((talent) => {
                          const isSelected = selectedStrengths.find(s => s.talent_id === talent.talent_id);
                          return (
                            <Button
                              key={talent.talent_id}
                              variant="ghost"
                              size="sm"
                              className={`w-full justify-start text-left h-auto py-1.5 ${isSelected ? 'opacity-50' : ''}`}
                              onClick={() => addStrength(talent.talent_id)}
                              disabled={isSelected}
                            >
                              <span className="truncate">{talent.name}</span>
                            </Button>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>

            {/* Selected strengths */}
            <div>
              <Label className="mb-2 block">Selected Strengths ({selectedStrengths.length}/10)</Label>
              <ScrollArea className="h-[300px] border rounded-lg p-2">
                {selectedStrengths.length > 0 ? (
                  <div className="space-y-2">
                    {selectedStrengths.map((s, index) => {
                      const talent = getTalentById(s.talent_id);
                      return (
                        <div 
                          key={s.talent_id}
                          className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800"
                        >
                          <span className="w-6 h-6 rounded-full bg-violet-100 dark:bg-violet-900/30 text-violet-600 text-xs font-bold flex items-center justify-center">
                            {index + 1}
                          </span>
                          <span className="flex-1 truncate text-sm">{talent?.name}</span>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-6 h-6"
                              onClick={() => moveStrength(s.talent_id, 'up')}
                              disabled={index === 0}
                            >
                              ↑
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-6 h-6"
                              onClick={() => moveStrength(s.talent_id, 'down')}
                              disabled={index === selectedStrengths.length - 1}
                            >
                              ↓
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="w-6 h-6 text-red-500 hover:text-red-600"
                              onClick={() => removeStrength(s.talent_id)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-8">
                    Click strengths to add them
                  </p>
                )}
              </ScrollArea>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStrengthDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-violet-600 hover:bg-violet-700"
              onClick={handleSaveStrengths}
              disabled={selectedStrengths.length === 0}
              data-testid="save-strengths-btn"
            >
              Save Strengths
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Member Dialog */}
      <AddMemberDialog 
        open={showAddMemberDialog}
        onOpenChange={setShowAddMemberDialog}
        orgId={currentOrg?.org_id}
        teamId={teamId}
        existingMembers={members}
        onSuccess={fetchTeamData}
      />
    </div>
  );
};

// Add Member Dialog Component
const AddMemberDialog = ({ open, onOpenChange, orgId, teamId, existingMembers, onSuccess }) => {
  const [orgMembers, setOrgMembers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedRole, setSelectedRole] = useState('member');
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    const fetchOrgMembers = async () => {
      if (!open || !orgId) return;
      
      setLoading(true);
      try {
        const response = await import('../lib/api').then(m => m.orgApi.listMembers(orgId));
        setOrgMembers(response.data);
      } catch (error) {
        console.error('Failed to load org members', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchOrgMembers();
  }, [open, orgId]);

  const handleAddMember = async () => {
    if (!selectedUserId) {
      toast.error('Please select a member');
      return;
    }

    setAdding(true);
    try {
      await teamApi.addMember(orgId, teamId, {
        team_id: teamId,
        user_id: selectedUserId,
        role: selectedRole
      });
      toast.success('Member added to team!');
      onOpenChange(false);
      setSelectedUserId('');
      setSelectedRole('member');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setAdding(false);
    }
  };

  // Filter out users already in the team
  const existingUserIds = existingMembers.map(m => m.user_id);
  const availableMembers = orgMembers.filter(m => !existingUserIds.includes(m.user_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }}>Add Team Member</DialogTitle>
          <DialogDescription>
            Select an organization member to add to this team
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
            </div>
          ) : availableMembers.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500">No available members to add</p>
              <p className="text-sm text-slate-400 mt-1">All organization members are already in this team</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label>Select Member</Label>
                <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                  <SelectTrigger data-testid="select-member-dropdown">
                    <SelectValue placeholder="Choose a member..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableMembers.map((member) => (
                      <SelectItem key={member.user_id} value={member.user_id}>
                        <div className="flex items-center gap-2">
                          <span>{member.user?.name || 'Unknown'}</span>
                          <span className="text-slate-400 text-xs">({member.user?.email})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Team Role</Label>
                <Select value={selectedRole} onValueChange={setSelectedRole}>
                  <SelectTrigger data-testid="select-role-dropdown">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="team_admin">Team Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            className="bg-violet-600 hover:bg-violet-700"
            onClick={handleAddMember}
            disabled={!selectedUserId || adding}
            data-testid="confirm-add-team-member-btn"
          >
            {adding ? 'Adding...' : 'Add Member'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TeamDetailPage;
